import { useTranslation } from "react-i18next";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, PinOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";
import type { IChatMessage } from "@/types/(my-space)/chat.interface";
import { messageContentPreview, personName } from "../_lib/chat-labels";

interface PinnedListProps {
  /** In the bar's order. */
  pins: IChatMessage[];
  /** Whoever may write may reorder and unpin. */
  canEdit: boolean;
  onShow: (messageId: number) => void;
  onUnpin: (m: IChatMessage) => void;
  /** Every pinned id, in its new order. */
  onReorder: (messageIds: number[]) => void;
}

/** Every pin of the chat; drag a row (or focus its handle + arrows) to reorder for everyone. */
export const PinnedList = ({ pins, canEdit, onShow, onUnpin, onReorder }: PinnedListProps) => {
  const { t } = useTranslation("chat");
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const ids = pins.map((p) => p.id);
    const next = arrayMove(ids, ids.indexOf(Number(active.id)), ids.indexOf(Number(over.id)));
    onReorder(next);
  };

  return (
    <div className="flex flex-col">
      <div className="px-3 pt-3 pb-1">
        <p className="text-sm font-semibold text-foreground">{t("pinned.list_title")}</p>
        {canEdit && pins.length > 1 ? (
          <p className="text-xs text-muted-foreground">{t("pinned.drag_hint")}</p>
        ) : null}
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={pins.map((p) => p.id)} strategy={verticalListSortingStrategy}>
          <ul className="flex flex-col p-1.5">
            {pins.map((p) => (
              <PinnedRow
                key={p.id}
                message={p}
                sortable={canEdit && pins.length > 1}
                onShow={onShow}
                onUnpin={canEdit ? onUnpin : undefined}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
    </div>
  );
};

const PinnedRow = ({
  message: m,
  sortable,
  onShow,
  onUnpin,
}: {
  message: IChatMessage;
  sortable: boolean;
  onShow: (messageId: number) => void;
  onUnpin?: (m: IChatMessage) => void;
}) => {
  const { t } = useTranslation("chat");
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: m.id, disabled: !sortable });
  const preview = messageContentPreview(t, {
    type: m.type,
    body: m.body,
    metadata: m.metadata,
    attachmentCount: m.attachments.length,
    isDeleted: m.isDeleted,
  });
  const image = m.type === "image" ? m.attachments[0]?.fileUrl : undefined;
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        "flex items-center gap-1 rounded-sm bg-background",
        isDragging && "relative z-10 shadow-md ring-1 ring-border",
      )}
    >
      {sortable ? (
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          aria-label={t("pinned.reorder", { message: preview })}
          className="flex size-8 shrink-0 cursor-grab touch-none items-center justify-center rounded-sm text-muted-foreground hover:bg-muted active:cursor-grabbing"
        >
          <GripVertical className="size-4" />
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => onShow(m.id)}
        className={cn(
          "flex min-w-0 flex-1 items-center gap-2.5 rounded-sm py-2 pe-2 text-start hover:bg-muted",
          !sortable && "ps-2",
        )}
      >
        {image ? <img src={image} alt="" className="size-8 shrink-0 rounded object-cover" /> : null}
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-xs font-medium text-muted-foreground">
            {m.sender ? personName(t, m.sender) : t("system.someone")}
          </span>
          <span dir="auto" className="truncate text-sm text-foreground">
            {preview}
          </span>
        </span>
      </button>
      {onUnpin ? (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => onUnpin(m)}
          aria-label={t("pinned.unpin")}
          title={t("pinned.unpin")}
        >
          <PinOff className="size-4" />
        </Button>
      ) : null}
    </li>
  );
};
